import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRole } from '../../context/RoleContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import { useLoading } from '../../context/LoadingContext';
import Breadcrumb from '../common/Breadcrumb';
import { useRoutePath } from '../../utils/getRoutePath';

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Role Name is required')
    .min(2, 'Role Name must be at least 2 characters')
    .max(50, 'Role Name must not exceed 50 characters'),
  active: yup.boolean()
});

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addRole, updateRole, getRoleById ,formErrors} = useRole();
  const { showLoading, hideLoading } = useLoading();
  const [isLoading, setIsLoading] = useState(false);
  const isSubmitting = useRef(false);
  const getRoutePath = useRoutePath();
   
  // Find the role to edit if in edit mode
  const roleToEdit = React.useMemo(() => {
    if (id) {
      const found = getRoleById(id);
      return found;
    }
    return null;
  }, [id, getRoleById]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      active: true
    },
    mode: 'onChange'
  });

  // Initialize form data when role data is loaded
  React.useEffect(() => {
    if (roleToEdit) {
      reset({
        name: roleToEdit.name,
        active: roleToEdit.active
      });
    }
  }, [roleToEdit, reset]);

  
  // Handle form submission
  const onSubmit = useCallback(async (data) => {
    if (isSubmitting.current) return;
    
    isSubmitting.current = true;
    setIsLoading(true);
    showLoading();
    
    try {
      let response;
      if (id) {
        response = await updateRole(id, data);
      } else {
        response = await addRole(data);
      }
  
      if (response?.status === 200) {
        Swal.fire({
          title: 'Success',
          text: `Role ${id ? 'updated' : 'created'} successfully!`,
          icon: 'success'
        }).then(() => {
          navigate(getRoutePath('/roles'));
        });
      } else {
        if (response?.errors) {
          Object.entries(response.errors).forEach(([field, message]) => {
            setError(field, {
              type: 'manual',
              message,
            });
          });
        } else if (response?.error?.message) {
          setError('general', {
            type: 'manual',
            message: response.error.message,
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: 'An error occurred while saving the role',
            icon: 'error'
          });
        }
      }
    } catch (error) {
      
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving the role',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
      hideLoading();
      isSubmitting.current = false;
    }
  }, [id, addRole, updateRole, navigate, setError, showLoading, hideLoading,getRoutePath]);
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <div className="mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Role' : 'Create New Role'}
        </h1>
      </div>
    
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mt-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Role Name *
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.name 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="Enter Role Name"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  {...register('active')}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            </div>

            {errors.general && (
              <p className="mt-2 text-sm text-red-600">{errors.general.message}</p>
            )}

          {formErrors?.general && (
            <div className="text-red-600 text-sm mt-2">{formErrors.general}</div>
          )}

            <div className="flex justify-end space-x-3">
              
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex justify-center rounded-md border border-transparent bg-[#009efb] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : id ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleForm; 